import React, { useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { linearGradientDef } from '@nivo/core';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { ShowChartRounded } from '@mui/icons-material';
import dayjs from 'dayjs';

const FONT_MONO = "'Roboto Mono', Consolas, monospace";

// One small figure in the header (total, peak, last hour)
const Figure = ({ label, value, hint }) => {
  const theme = useTheme();
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.palette.text.secondary }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.25, color: theme.palette.secondary.main }}>
        {value}
      </Typography>
      {hint && (
        <Typography noWrap sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary }}>{hint}</Typography>
      )}
    </Box>
  );
};

/**
 * Unique flights detected per hour over the last 48 hours (dashboard bottom card).
 * data: [{ updatedAt: <start of the hour>, hour_data: <count> }]
 */
const HourStatisticChart = ({ data }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;
  const muted = theme.palette.text.secondary;
  const gridLine = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';

  const points = useMemo(
    () => (data || []).map((item) => {
      const at = dayjs(item.updatedAt);
      return { x: at.toISOString(), y: Number(item.hour_data) || 0, at };
    }),
    [data]
  );

  const stats = useMemo(() => {
    if (points.length === 0) return null;
    const total = points.reduce((sum, p) => sum + p.y, 0);
    const peak = points.reduce((best, p) => (p.y > best.y ? p : best), points[0]);
    const last = points[points.length - 1];
    return { total, peak, last };
  }, [points]);

  // a label every 6 hours keeps the axis readable
  const tickValues = useMemo(
    () => points.filter((p) => p.at.hour() % 6 === 0).map((p) => p.x),
    [points]
  );

  const formatTick = (x) => {
    const at = dayjs(x);
    return at.hour() === 0 ? at.format('D MMM') : at.format('HH:00');
  };

  if (!data || data.length === 0) {
    return (
      <Box sx={{ height: 320, display: 'grid', placeItems: 'center', color: muted }}>
        Loading chart…
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', minHeight: 320, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Header: title + key figures */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: accent,
              backgroundColor: alpha(accent, 0.12),
              border: `1px solid ${alpha(accent, 0.4)}`,
            }}
          >
            <ShowChartRounded sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ color: theme.palette.secondary[100], fontWeight: 700, lineHeight: 1.2 }}>
              Detected Flights by Hour
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: muted }}>
              Unique flights seen each hour · last 48 hours
            </Typography>
          </Box>
        </Box>

        {stats && (
          <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3.5 } }}>
            <Figure label="Total" value={stats.total} hint="48 hours" />
            <Figure label="Peak" value={stats.peak.y} hint={stats.peak.y ? stats.peak.at.format('ddd HH:00') : '—'} />
            <Figure label="This hour" value={stats.last.y} hint={`since ${stats.last.at.format('HH:00')}`} />
          </Box>
        )}
      </Box>

      {/* Chart */}
      <Box sx={{ flex: 1, minHeight: 240 }}>
        <ResponsiveLine
          data={[{ id: 'Flights', data: points }]}
          margin={{ top: 12, right: 16, bottom: 36, left: 44 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
          curve="monotoneX"
          colors={[accent]}
          lineWidth={2}
          enableArea
          areaOpacity={1}
          areaBaselineValue={0}
          defs={[
            linearGradientDef('flightsGradient', [
              { offset: 0, color: accent, opacity: 0.35 },
              { offset: 100, color: accent, opacity: 0 },
            ]),
          ]}
          fill={[{ match: '*', id: 'flightsGradient' }]}
          enablePoints={false}
          enableGridX={false}
          enableGridY
          gridYValues={4}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: 10,
            tickValues,
            format: formatTick,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 10,
            tickValues: 4,
            format: (v) => (Number.isInteger(v) ? v : ''),
          }}
          useMesh
          enableCrosshair
          crosshairType="x"
          tooltip={({ point }) => (
            <Box
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                whiteSpace: 'nowrap',
                backgroundColor: alpha(theme.palette.background.alt, 0.97),
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : theme.palette.neutral[200]}`,
                boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
              }}
            >
              <Typography sx={{ fontSize: '0.7rem', color: muted, lineHeight: 1.3 }}>
                {dayjs(point.data.x).format('ddd D MMM, HH:00')}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, lineHeight: 1.3, color: theme.palette.text.primary }}>
                <Box component="span" sx={{ color: accent }}>{point.data.y}</Box> flights
              </Typography>
            </Box>
          )}
          theme={{
            text: { fill: muted, fontSize: 11, fontFamily: FONT_MONO },
            axis: {
              domain: { line: { stroke: 'transparent' } },
              ticks: { text: { fill: muted, fontSize: 11, fontFamily: FONT_MONO } },
            },
            grid: { line: { stroke: gridLine, strokeDasharray: '4 4' } },
            crosshair: { line: { stroke: accent, strokeOpacity: 0.6, strokeWidth: 1, strokeDasharray: '3 3' } },
          }}
        />
      </Box>
    </Box>
  );
};

export default HourStatisticChart;
